import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { isFreeEmail } from 'free-email-domains-list';

import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs';

import { PortalRole } from 'src/app/models/portal-role.model';
import { Permission } from 'src/app/shared/constants/permissions';
import * as fromStore from 'src/app/store/';
import { OktaUserRequest, OktaUserService } from '../services/okta-user';

import { UserDialogData } from '../user-dialog.model';

interface UserFormData {
  id?: string;
  accountName: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  portalRole: { Id: string; Permissions__c: string; Name: string };
  oktaId?: string;
  sfContactId?: string;
}

@Component({
  selector: 'app-create-edit-user-dialog',
  templateUrl: 'create-edit-user-dialog.component.html',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatOptionModule,
    CommonModule,
    NgxPermissionsModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEditUserDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<CreateEditUserDialogComponent>);

  readonly dialogData = inject<UserDialogData | null>(MAT_DIALOG_DATA);

  readonly Permission = Permission;

  userForm: FormGroup;

  isEditMode = false;

  isInternalOnly = signal(false);

  isSubmitting$ = new BehaviorSubject(false);

  errorMessage: string | null = null;

  hasSubmissionError$ = new BehaviorSubject(false);

  private store = inject(Store<fromStore.State>);

  portalRoles$: Observable<PortalRole[]> = this.store.select(
    fromStore.getPortalRoles,
  );

  private destroy$ = new Subject<void>();

  private usernameBehaviorConfigured = false;

  get firstName(): FormControl {
    return this.userForm.get('firstName') as FormControl;
  }

  get lastName(): FormControl {
    return this.userForm.get('lastName') as FormControl;
  }

  get email(): FormControl {
    return this.userForm.get('email') as FormControl;
  }

  get username(): FormControl {
    return this.userForm.get('username') as FormControl;
  }

  get portalRole(): FormControl {
    return this.userForm.get('portalRole') as FormControl;
  }

  comparePortalRole: (a: PortalRole | null, b: PortalRole | null) => boolean = (
    a,
    b,
  ) => (!!a && !!b ? a.Id === b.Id : a === b);

  private fb = inject(FormBuilder);

  private oktaUserService = inject(OktaUserService);

  private changeDetectorRef = inject(ChangeDetectorRef);

  private permissionsService = inject(NgxPermissionsService);

  private readonly allowedCorporateDomains = [
    'comcast.com',
    'cable.comcast.com',
    'xfinity.com',
    'xfinitymail.com',
    'nbcuni.com',
    'nbc.com',
    'nbcuniversal.com',
  ];

  private isDomainOrSubdomain(domain: string, base: string): boolean {
    return domain === base || domain.endsWith(`.${base}`);
  }

  private nonPublicEmailValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const email = String(control.value ?? '')
      .trim()
      .toLowerCase();
    const at = email.lastIndexOf('@');
    if (at < 0 || at === email.length - 1) return null;

    const domain = email.slice(at + 1);

    if (
      this.allowedCorporateDomains.some((d) =>
        this.isDomainOrSubdomain(domain, d),
      )
    ) {
      return null;
    }

    return isFreeEmail(email) ? { publicEmailDomain: true } : null;
  };

  async ngOnInit(): Promise<void> {
    this.isEditMode = !!this.dialogData?.Id;

    this.userForm = this.fb.group({
      id: [this.dialogData?.Id],
      accountName: [this.dialogData?.Account?.Name],
      oktaId: [this.dialogData?.OktaUserId__c],
      sfContactId: [this.dialogData?.Id],

      firstName: [
        this.dialogData?.FirstName || '',
        [Validators.required, Validators.minLength(2)],
      ],
      lastName: [
        this.dialogData?.LastName || '',
        [Validators.required, Validators.minLength(2)],
      ],

      email: [
        { value: this.dialogData?.Email || '', disabled: this.isEditMode },
        [Validators.required, Validators.email, this.nonPublicEmailValidator],
      ],

      username: [
        {
          value:
            this.dialogData?.OktaUsername__c || this.dialogData?.Email || '',
          disabled: this.isEditMode,
        },
        [Validators.required, Validators.email],
      ],

      portalRole: [
        this.dialogData?.Portal_Role__r?.Id ?? null,
        Validators.required,
      ],
    });

    this.isInternalOnly.set(
      await this.permissionsService.hasPermission(Permission.NITEL_INTERNAL),
    );

    this.configureUsernameBehavior();

    this.isSubmitting$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isSubmitting) => {
        const controls = ['firstName', 'lastName', 'email', 'portalRole'];

        controls.forEach((c) => {
          const ctrl = this.userForm.get(c);
          if (!ctrl) return;
          if (isSubmitting) ctrl.disable({ emitEvent: false });
          else ctrl.enable({ emitEvent: false });
        });

        if (this.isEditMode) {
          this.username.disable({ emitEvent: false });
          return;
        }

        if (this.isInternalOnly()) {
          if (isSubmitting) this.username.disable({ emitEvent: false });
          else this.username.enable({ emitEvent: false });
        } else {
          this.username.disable({ emitEvent: false });
        }
      });

    this.portalRoles$.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      const matchingRole = roles.find(
        (role) => role.Id === this.dialogData?.Portal_Role__r?.Id,
      );
      this.userForm.patchValue({
        portalRole: matchingRole ?? null,
      });
    });

    if (this.isEditMode) {
      this.userForm.markAsPristine();
      this.userForm.markAsUntouched();
      this.userForm.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private configureUsernameBehavior(): void {
    if (this.usernameBehaviorConfigured) return;
    this.usernameBehaviorConfigured = true;

    if (this.isEditMode) {
      this.username.disable({ emitEvent: false });
      return;
    }

    if (this.isInternalOnly()) {
      const email = this.normalizeEmail(this.email.value);
      if (email && !this.username.value) {
        this.username.setValue(email, { emitEvent: false });
      }
      this.username.enable({ emitEvent: false });
      return;
    }

    this.username.disable({ emitEvent: false });
    this.username.setValue(this.normalizeEmail(this.email.value), {
      emitEvent: false,
    });

    this.email.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map((v) => this.normalizeEmail(v)),
        distinctUntilChanged(),
      )
      .subscribe((normalizedEmail) => {
        this.username.setValue(normalizedEmail, { emitEvent: false });
      });
  }

  setUsernameToEmail(): void {
    const email = this.normalizeEmail(this.email.value);
    this.username.setValue(email, { emitEvent: false });
    this.username.markAsDirty();
    this.username.markAsTouched();
  }

  onSave(): void {
    this.userForm.markAllAsTouched();

    if (!this.isEditMode && !this.isInternalOnly()) {
      this.username.setValue(this.normalizeEmail(this.email.value), {
        emitEvent: false,
      });
    }

    if (!this.userForm.valid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      this.hasSubmissionError$.next(true);
      this.isSubmitting$.next(false);
      return;
    }

    if (!this.isUserRoleValid()) return;

    const formData = this.userForm.getRawValue() as UserFormData;

    if (this.isEditMode) {
      this.updateUser(formData);
    } else {
      this.inviteOktaUser(formData);
    }
  }

  private normalizeEmail(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private normalizeLogin(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private isUserRoleValid(): boolean {
    const selectedRole = this.userForm.get('portalRole')
      ?.value as PortalRole | null;

    const selectedId = selectedRole?.Id ?? null;
    const selectedName = selectedRole?.Name ?? null;

    const currentId = this.dialogData?.Portal_Role__r?.Id ?? null;
    const isRoleChanged = selectedId !== currentId;

    const adminLimitReached =
      selectedName === 'Admin' && (this.dialogData?.TotalAdmins ?? 0) >= 1;

    if (
      (!this.isEditMode && adminLimitReached) ||
      (this.isEditMode && adminLimitReached && isRoleChanged)
    ) {
      this.errorMessage =
        'Admin limit reached! Please remove an existing Admin to add another.';
      this.hasSubmissionError$.next(true);
      this.isSubmitting$.next(false);
      return false;
    }

    return true;
  }

  private inviteOktaUser(formData: UserFormData): void {
    this.isSubmitting$.next(true);
    const email = this.normalizeEmail(formData.email);

    const login = this.isInternalOnly()
      ? this.normalizeLogin(formData.username)
      : email;

    const oktaPayload: OktaUserRequest = {
      email,
      login,
      firstName: formData.firstName,
      lastName: formData.lastName,
      portalRoleId: formData.portalRole.Id,
      accountId: this.dialogData?.AccountId,
    };

    this.oktaUserService
      .createUser(oktaPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting$.next(false);
          this.hasSubmissionError$.next(false);

          setTimeout(() => this.reloadContacts(), 1000);

          this.dialogRef.close(formData);
        },
        error: (error) => {
          console.error('Error inviting user:', error);
          this.getErrorMessage(error);
        },
      });
  }

  private updateUser(formData: UserFormData): void {
    this.isSubmitting$.next(true);

    const email = this.normalizeEmail(formData.email ?? this.dialogData?.Email);
    const login = this.normalizeLogin(
      this.dialogData?.OktaUsername__c ?? email,
    );
    const oktaPayload: OktaUserRequest = {
      email,
      login,
      firstName: formData.firstName,
      lastName: formData.lastName,
      portalRoleId: formData.portalRole.Id,
      accountId: this.dialogData?.AccountId,
      oktaId: formData.oktaId || this.dialogData?.OktaUserId__c,
      sfContactId: formData.sfContactId || this.dialogData?.Id,
    };

    this.oktaUserService
      .updateUser(oktaPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting$.next(false);
          this.hasSubmissionError$.next(false);

          setTimeout(() => this.reloadContacts(), 1000);

          this.dialogRef.close(formData);
        },
        error: (error) => {
          console.error('Error updating user:', error);
          this.getErrorMessage(error);
        },
      });
  }

  private getErrorMessage(error: { message: string }): void {
    this.hasSubmissionError$.next(true);
    this.isSubmitting$.next(false);
    this.errorMessage = error.message || 'An error occurred. Please try again.';
    this.changeDetectorRef.detectChanges();
  }

  private reloadContacts(): void {
    this.store.dispatch(new fromStore.ClearContacts());
    this.store.dispatch(new fromStore.LoadContacts());
  }
}
